/**
 * BigQuery / worker analytics helpers for the NAR letter modal.
 * @module useNarLetterEvents
 */

import { useCallback, useEffect, useRef } from "react";
import { DocumentsStoreedInLocalStorage } from "../../../../data-model/docsStorageInLocalStorage";
import { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { getEventNameByDocTypeWithFallback } from "../../../../utils/documentTypeToEventName";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { NarLetterModalMetaData } from "../types";

/** Inputs for `useNarLetterEvents` (order, user, org, payer, modal metadata, logging flag). */
interface UseNarLetterEventsParams {
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  userId: string;
  userEmail: string;
  orgID: string;
  payerName: string;
  metaData: NarLetterModalMetaData | undefined;
  shouldLogEvent?: boolean;
}

/**
 * Centralises all BigQuery event logging for the NAR letter modal.
 *
 * On mount (when `shouldLogEvent` is true), logs a `MODAL_SHOWN` event once.
 * Subsequent user interactions are captured via the returned callback functions,
 * each of which enriches a shared base payload (patient, order, org, user) with
 * interaction-specific data before enqueueing through the API worker manager.
 *
 * @param params.generalOrderDetails - Current order; supplies `patient_id` and `order_id` for events.
 * @param params.userId - Authenticated user's ID.
 * @param params.userEmail - Authenticated user's email.
 * @param params.orgID - Current organisation ID.
 * @param params.payerName - Payer name from the single-order layout context.
 * @param params.metaData - Modal metadata containing `docType` for tagging events.
 * @param params.shouldLogEvent - Guard flag; when false, no events are emitted.
 * @returns Object with `logDocumentTitleUpdated`, `logCheckBoxClick`, `logUploadDocument`,
 *   `logDeleteDocument`, and `logDocUploaded` callbacks. `logDocUploaded` records
 *   `is_letter_selected_from_dropdown` in `additional_data` (true for dropdown, false for manual upload).
 */
export const useNarLetterEvents = ({
  generalOrderDetails,
  userId,
  userEmail,
  orgID,
  payerName,
  metaData,
  shouldLogEvent,
}: UseNarLetterEventsParams) => {
  const isEventLogged = useRef(false);
  const logEventTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const baseEventData = useCallback(
    () => ({
      patient_id:
        generalOrderDetails?.demographics?.patient_id ??
        generalOrderDetails?.demographics?.patient_mrn ??
        "",
      order_id: generalOrderDetails?.order_id ?? "",
      org_id: orgID ?? "",
      user_id: userId,
      user_email: userEmail,
    }),
    [generalOrderDetails, orgID, userId, userEmail],
  );

  useEffect(() => {
    if (shouldLogEvent && !isEventLogged.current) {
      apiWorkerManager.enqueueAndProcess({
        ...baseEventData(),
        event_name: EventsName.MODAL_SHOWN,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
          doc_type: metaData?.docType ?? "",
          payer_name: payerName,
        },
      });
      isEventLogged.current = true;
    }
  }, [shouldLogEvent]);

  const logDocumentTitleUpdated = useCallback(
    (docTitle: string) => {
      if (logEventTimeoutRef.current) {
        clearTimeout(logEventTimeoutRef.current);
      }
      logEventTimeoutRef.current = setTimeout(() => {
        apiWorkerManager.enqueueAndProcess({
          ...baseEventData(),
          event_name: EventsName.DOCUMENT_TITLE_UPDATED,
          additional_data: {
            screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
            doc_type: metaData?.docType ?? "",
            doc_title: docTitle,
            payer_name: payerName,
          },
        });
        logEventTimeoutRef.current = null;
      }, 850);
    },
    [baseEventData, metaData?.docType, payerName],
  );

  const logCheckBoxClick = useCallback(
    (
      medicineName: string,
      isChecked: boolean,
      docMetaData: DocumentsStoreedInLocalStorage[],
    ) => {
      if (!shouldLogEvent || !isEventLogged.current) return;

      const description = docMetaData.find(
        (doc) => doc.jcodeIdentifier === medicineName,
      )?.jcodeDescription;

      apiWorkerManager.enqueueAndProcess({
        ...baseEventData(),
        event_name:
          EventsName.CHECK_BOX_CLICKED +
          " (" +
          description +
          " - " +
          isChecked +
          ")",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
          doc_type: metaData?.docType ?? "",
          payer_name: payerName,
        },
      });
    },
    [shouldLogEvent, baseEventData, metaData?.docType, payerName],
  );

  const logUploadDocument = useCallback(() => {
    if (!shouldLogEvent) return;

    apiWorkerManager.enqueueAndProcess({
      ...baseEventData(),
      event_name: EventsName.UPLOAD_DOCUMENT,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
        doc_type: metaData?.docType ?? "",
        payer_name: payerName,
      },
    });
  }, [shouldLogEvent, baseEventData, metaData?.docType, payerName]);

  const logDeleteDocument = useCallback(() => {
    if (!shouldLogEvent) return;

    apiWorkerManager.enqueueAndProcess({
      ...baseEventData(),
      event_name: EventsName.DELETE_DOCUMENT_CLICKED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
        doc_type: metaData?.docType ?? "",
        payer_name: payerName,
      },
    });
  }, [shouldLogEvent, baseEventData, metaData?.docType, payerName]);

  const logDocUploaded = useCallback(
    (
      docTitle: string,
      docPath: string,
      isLetterSelectedFromDropdown: boolean,
    ) => {
      apiWorkerManager.enqueueAndProcess({
        ...baseEventData(),
        event_name: getEventNameByDocTypeWithFallback(metaData?.docType ?? ""),
        additional_data: {
          screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
          doc_type: metaData?.docType ?? "",
          doc_title: docTitle,
          doc_path: docPath,
          payer_name: payerName,
          is_letter_selected_from_dropdown: isLetterSelectedFromDropdown,
        },
      });
    },
    [baseEventData, metaData?.docType, payerName],
  );

  useEffect(
    () => () => {
      if (logEventTimeoutRef.current) clearTimeout(logEventTimeoutRef.current);
    },
    [],
  );

  return {
    logDocumentTitleUpdated,
    logCheckBoxClick,
    logUploadDocument,
    logDeleteDocument,
    logDocUploaded,
  };
};
